// The person card (17-people.md, "Person card"): one floating card at a time, opened from a name,
// a picture, a mention or a member row. Message, More (Block/Unblock), the membership notices and
// moderation in a channel. The role chip joins with 18; Write and Meet with mail and calendar. The
// blurred cover, the "Removed" notice and Invite for someone who left were candidates to drop.
import { EventType, type MatrixClient, type Room } from 'matrix-js-sdk';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Button, IconButton } from '../blocks/Button';
import { Confirm } from '../blocks/Dialog';
import { Field } from '../blocks/Field';
import { icons } from '../blocks/icons';
import { Menu, MenuItem, MenuLine } from '../blocks/Menu';
import { toast } from '../blocks/Toast';
import { Spinner } from '../blocks/Wait';
import { describe } from '../core/errors';
import { idColour, messageTime } from '../core/format';
import { clock } from '../core/settings';
import { t } from '../i18n';
import { chatIcons } from '../chat/icons';
import { ImageViewer } from '../chat/viewers';
import { httpOf } from '../chat/media';
import { PersonPicture, usePresence } from './presence';
import { openDirectChat } from './direct';
import { personName, personPicture, shownName } from './names';
import { isDirect, useRoomState } from '../session/rooms';
import { outranks, powerOf, roleName, ROLES } from '../settings/roles';
import { openRoomSettings } from '../settings/RoomSettings';
import './people.css';
import { createPortal } from 'react-dom';
import { topModal } from '../blocks/Dialog';

let open: ((p: { userId: string; anchor: HTMLElement | null; room?: Room }) => void) | undefined;
/** Opens the card for a person beside what was clicked, in a room or alone. */
export const showPerson = (userId: string, anchor: HTMLElement | null, room?: Room) => open?.({ userId, anchor, room });

/** Where the card lives in the frame. */
export function PersonHost({ client }: { client: MatrixClient }) {
  const [person, setPerson] = useState<{ userId: string; anchor: HTMLElement | null; room?: Room; layer?: HTMLElement }>();
  useEffect(() => {
    // Opened from inside a modal window (settings → Members): the card goes inside it, never inert.
    open = (p) => setPerson({ ...p, layer: topModal() });
    return () => void (open = undefined);
  }, []);
  if (!person) return null;
  const { layer, ...where } = person;
  const card = <PersonCard key={person.userId} client={client} {...where} onClose={() => setPerson(undefined)} />;
  return layer ? createPortal(card, layer) : card;
}

const ignored = (client: MatrixClient) => client.getIgnoredUsers();

/** The role chip and its menu (17, "Changing a role"): the fixed roles, then "Manage permissions". */
function RoleChip({ client, room, userId, name, onDone }: { client: MatrixClient; room: Room; userId: string; name: string; onDone: () => void }) {
  useRoomState(room); // rights follow at once (also one's own, right after lowering it)
  const me = client.getUserId()!;
  const chip = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string>();
  const power = powerOf(room, userId);
  const mine = powerOf(room, me);
  const mayChange = room.currentState.maySendStateEvent(EventType.RoomPowerLevels, me) && (userId === me || outranks(room, me, userId));
  const [asking, setAsking] = useState<number>();
  const give = (p: number) => {
    setBusy(true);
    setFailed(undefined);
    return client.setPowerLevel(room.roomId, userId, p).then(
      () => setBusy(false),
      (e) => (setBusy(false), setFailed(describe(e, 'save').text)),
    );
  };
  return (
    <>
      <button ref={chip} type="button" className={`chip${power === Infinity ? ' founder' : ''}${failed ? ' failed' : ''}`} aria-haspopup="menu" aria-pressed={open} disabled={busy} onClick={() => setOpen(true)}>
        {busy && <Spinner size={14} />}
        {roleName(power)}
      </button>
      {open && (
        <Menu label={roleName(power)} anchor={chip.current} onClose={() => setOpen(false)}>
          {failed && <p className="menu-error">{failed}</p>}
          {power !== Infinity &&
            ROLES.map((p) => (
              <MenuItem
                key={p}
                pressed={p === power}
                disabled={!mayChange || (mine !== Infinity && p > mine)}
                onSelect={() => {
                  if (p === power) return;
                  // What the person can't undo themselves asks first (the owner): lowering oneself, giving Admin.
                  if ((userId === me && p < power) || (userId !== me && (p === 100 || p === mine))) setAsking(p);
                  else void give(p);
                }}
              >
                {roleName(p)}
              </MenuItem>
            ))}
          {power !== Infinity && <MenuLine />}
          <MenuItem icon={chatIcons.keep} onSelect={() => (onDone(), openRoomSettings(room, 'permissions'))}>
            {t('people.managePermissions')}
          </MenuItem>
        </Menu>
      )}
      {asking !== undefined && (
        <Confirm
          title={userId === me ? t('people.lowerTitle') : t('people.adminTitle', { name })}
          text={userId === me ? t('people.lowerText') : t('people.adminText', { name })}
          action={t(userId === me ? 'people.lower' : 'people.makeAdmin')}
          destructive
          narrow
          onConfirm={() => give(asking)}
          onClose={() => setAsking(undefined)}
        />
      )}
    </>
  );
}

/** Blocks or unblocks, from the list as it is at that moment (17, Blocking). */
export const setBlocked = (client: MatrixClient, userId: string, block: boolean) =>
  client.setIgnoredUsers(block ? [...new Set([...ignored(client), userId])] : ignored(client).filter((u) => u !== userId));

function PersonCard({ client, userId, anchor, room, onClose }: { client: MatrixClient; userId: string; anchor: HTMLElement | null; room?: Room; onClose: () => void }) {
  usePresence(client);
  const me = client.getUserId()!;
  const ref = useRef<HTMLDivElement>(null);
  const member = room?.getMember(userId);
  const [profile, setProfile] = useState<{ displayname?: string; avatar_url?: string }>();
  const [viewing, setViewing] = useState(false);
  const [busy, setBusy] = useState<string>();
  const [error, setError] = useState<string>();
  const [reason, setReason] = useState('');
  const more = useRef<HTMLButtonElement>(null);
  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => {
    if (!member) void client.getProfileInfo(userId).then(setProfile, () => {});
  }, [client, userId, member]);

  // Beside what opened it; Escape and a press outside close it; the focus comes back.
  useLayoutEffect(() => {
    const card = ref.current!;
    card.showPopover?.();
    const r = anchor?.getBoundingClientRect() ?? { left: innerWidth / 2, right: innerWidth / 2, top: 80 };
    const left = r.right + 8 + card.offsetWidth < innerWidth ? r.right + 8 : r.left - card.offsetWidth - 8;
    card.style.left = `${Math.max(8, left)}px`;
    card.style.top = `${Math.max(8, Math.min(r.top, innerHeight - card.offsetHeight - 8))}px`;
    card.focus();
    const toggled = (e: Event) => (e as ToggleEvent).newState === 'closed' && onClose();
    card.addEventListener('toggle', toggled);
    return () => {
      card.removeEventListener('toggle', toggled);
      anchor?.focus?.();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const name = (!member && shownName(profile?.displayname)) || personName(client, userId, room);
  const mxc = member?.getMxcAvatarUrl() || (!member && profile?.avatar_url) || personPicture(client, userId, room);
  const own = userId === me;
  const blocked = ignored(client).includes(userId);
  const state = room?.currentState;
  const membership = member?.membership;
  const event = member?.events.member;
  const higher = !!room && outranks(room, me, userId);
  const may = (action: 'kick' | 'ban' | 'invite') => !!room && !!state && (powerOf(room, me) === Infinity || state.hasSufficientPowerLevelFor(action, powerOf(room, me)));
  const act = async (key: string, run: () => Promise<unknown>) => {
    setBusy(key);
    setError(undefined);
    try {
      await run();
      setReason('');
    } catch (e) {
      setError(describe(e, 'general').text);
    } finally {
      setBusy(undefined);
    }
  };
  const why = reason.trim() || undefined;
  const canRemove = membership === 'join' && may('kick') && higher && !own;
  const canBan = membership !== 'ban' && may('ban') && higher && !own;

  return (
    <div ref={ref} className="person-card" role="dialog" aria-label={t('people.person')} popover="auto" tabIndex={-1} data-desyr-person-card="">
      <div className="person-cover" style={{ background: `var(--name-${idColour(userId)})` }} />
      <div className="person-head">
        {mxc ? (
          <button type="button" className="person-big" aria-label={t('people.picture', { name })} onClick={() => setViewing(true)}>
            <PersonPicture client={client} userId={userId} name={name} mxc={mxc} size={58} />
          </button>
        ) : (
          <span className="person-big">
            <PersonPicture client={client} userId={userId} name={name} size={58} />
          </span>
        )}
      </div>
      <h2 className="person-name">{name}</h2>
      {(!own || room) && (
        <div className="person-actions">
          {!own && (
            <Button small variant="filled" onClick={() => (onClose(), void openDirectChat(client, userId, name).catch((e) => toast(e.message)))}>
              {t('people.message')}
            </Button>
          )}
          {room && !isDirect(client, room.roomId) && <RoleChip client={client} room={room} userId={userId} name={name} onDone={onClose} />}
          {room && !own && (
            <>
              <button ref={more} type="button" className="chip" aria-label={t('people.more')} aria-haspopup="menu" disabled={busy === 'block'} onClick={() => setMoreOpen(true)}>
                {busy === 'block' ? <Spinner size={14} /> : '⋯'}
              </button>
              {moreOpen && (
                <Menu label={t('people.more')} anchor={more.current} onClose={() => setMoreOpen(false)}>
                  <MenuItem icon={chatIcons.block} destructive onSelect={() => void act('block', () => setBlocked(client, userId, !blocked))}>
                    {t(blocked ? 'people.unblock' : 'people.block')}
                  </MenuItem>
                </Menu>
              )}
            </>
          )}
        </div>
      )}
      {room && blocked && (
        <div className="person-notice critical">
          <strong>{t('people.blocked')}</strong>
          <p>{t('people.blockedLine')}</p>
        </div>
      )}
      {room && event && (membership === 'ban' || membership === 'invite') && (
        <div className={`person-notice ${membership === 'ban' ? 'critical' : 'success'}`}>
          <div className="person-notice-head">
            <strong>{t(membership === 'ban' ? 'people.banned' : 'people.invited')}</strong>
            <time>{messageTime(new Date(event.getTs()), clock())}</time>
          </div>
          <p>{t('people.by', { name: personName(client, event.getSender()!, room) })}</p>
          <p>{event.getContent().reason ? t('people.reason', { reason: event.getContent().reason }) : <em>{t('people.noReason')}</em>}</p>
          {membership === 'ban' && may('ban') && (
            <Button small busy={busy === 'unban'} onClick={() => void act('unban', () => client.unban(room.roomId, userId))}>
              {t('people.unban')}
            </Button>
          )}
          {membership === 'invite' && may('kick') && higher && (
            <Button small busy={busy === 'cancel'} onClick={() => void act('cancel', () => client.kick(room.roomId, userId))}>
              {t('people.cancelInvite')}
            </Button>
          )}
        </div>
      )}
      {room && (canRemove || canBan) && (
        <div className="person-moderation">
          <h3>{t('people.moderation')}</h3>
          <Field label={t('people.reasonField')} value={reason} disabled={!!busy} onChange={(e) => setReason(e.target.value)} />
          <div className="person-actions">
            {canRemove && (
              <Button small variant="critical-soft" busy={busy === 'remove'} disabled={!!busy} onClick={() => void act('remove', () => client.kick(room.roomId, userId, why))}>
                {t('people.remove')}
              </Button>
            )}
            {canBan && (
              <Button small variant="critical" busy={busy === 'ban'} disabled={!!busy} onClick={() => void act('ban', () => client.ban(room.roomId, userId, why))}>
                {t('people.ban')}
              </Button>
            )}
          </div>
        </div>
      )}
      {error && (
        <p className="field-error" role="alert">
          <strong>{error}</strong>
        </p>
      )}
      <IconButton className="person-close" label={t('common.close')} icon={icons.close} size={18} onClick={onClose} />
      {viewing && mxc && <ImageViewer src={httpOf(client, mxc)} name={name} onClose={() => setViewing(false)} />}
    </div>
  );
}

